// engine-exception: a major incident is run, not browsed — the workbench composes one aggregate read (parent, roles, children, signals, comms log) with the two actions that matter during an outage, post an update and resolve; the generic detail screen has no comms log and no broadcast
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAction, useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton, Stat } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Icon } from '../../engine/design/icons';

// The major incident workbench (docs/servicedesk-v2-proposal.md P9). Everything the two people
// running an outage need on one screen: who is commanding, who is talking to customers, what is
// attached, what the alerts say, and the one box that tells everybody at once.

interface Update { id: string; seq: number; kind: string; body: string; audience: string; is_public: boolean; recipients: number; published_at: string }
interface Child { id: string; ref: string | null; summary: string | null; status_id: string | null }
interface Signal { id: string; summary: string; severity: string; asset_name: string | null; occurrences: number; state: string }
interface Workbench {
  incident: { id: string; ref: string | null; title: string; severity: string; state: string; is_public: boolean; next_update_at: string | null; problem_ticket_id: string | null; cause: string | null };
  ticket: { id: string; ref: string | null } | null;
  commander: { name: string } | null;
  comms: { name: string } | null;
  children: Child[];
  updates: Update[];
  signals: Signal[];
  affected_contacts: number;
  update_overdue: boolean;
}

const STATE_TONE: Record<string, 'neg' | 'warn' | 'pos' | 'muted'> = { declared: 'neg', mitigated: 'warn', resolved: 'pos', closed: 'muted' };
const STATE_LABEL: Record<string, string> = { declared: 'Live', mitigated: 'Under control', resolved: 'Resolved', closed: 'Closed' };
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function IncidentWorkbenchScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const wb = useApiQuery<Workbench>(['incident-workbench', id], `/v1/major-incidents/${id}/workbench`, { staleTime: 0 });
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<'internal' | 'affected'>('affected');

  const post = useAction<{ recipients: number }>('major-incidents', 'post', { toast: { success: false } });
  const mitigate = useAction('major-incidents', 'mitigate', { toast: { success: 'Recorded as under control' } });
  const resolve = useAction('major-incidents', 'resolve', { toast: { success: 'Resolved — everyone has been told' } });

  if (wb.error) return <ErrorBanner error={wb.error} />;
  if (!wb.data) return <Skeleton lines={8} />;
  const { incident, children, updates, signals } = wb.data;
  const over = incident.state === 'resolved' || incident.state === 'closed';

  const send = async (kind: 'update' | 'resolved') => {
    const text = body.trim();
    if (!text) return;
    if (kind === 'resolved') await resolve.mutateAsync({ id, body: { note: text } });
    else await post.mutateAsync({ id, body: { body: text, audience } });
    setBody('');
    await wb.refetch();
  };

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      <PageHeader
        title={incident.title}
        subtitle={wb.data.ticket?.ref ?? undefined}
        actions={
          <div className="hf-row" style={{ gap: 'var(--s-1)' }}>
            <Badge tone={STATE_TONE[incident.state] ?? 'muted'}>{STATE_LABEL[incident.state] ?? incident.state}</Badge>
            {incident.is_public && <Badge tone="info">On the status page</Badge>}
            {wb.data.update_overdue && <Badge tone="neg">Update overdue</Badge>}
            {wb.data.ticket && (
              <Button variant="ghost" onClick={() => navigate(`/tickets/${wb.data!.ticket!.id}`)}>
                <Icon name="ticket" /> The ticket
              </Button>
            )}
            {!over && (
              <Button variant="ghost" onClick={() => { void mitigate.mutateAsync({ id, body: {} }).then(() => wb.refetch()); }} disabled={incident.state !== 'declared'}>
                Under control
              </Button>
            )}
          </div>
        }
      />

      <div className="hf-row" style={{ gap: 'var(--s-3)', flexWrap: 'wrap' }}>
        <Stat label="Commander" value={wb.data.commander?.name ?? 'Nobody yet'} />
        <Stat label="Comms" value={wb.data.comms?.name ?? 'Nobody yet'} />
        <Stat label="Tickets attached" value={String(children.length)} />
        <Stat label="People affected" value={String(wb.data.affected_contacts)} />
        <Stat label="Alerts folded in" value={String(signals.length)} />
        <Stat label="Next update" value={incident.next_update_at ? when(incident.next_update_at) : '—'} />
      </div>

      {!over && (
        <section className="hf-card" style={{ padding: 'var(--s-2)' }} aria-label="Tell everyone">
          <textarea
            className="hf-input"
            rows={3}
            value={body}
            aria-label="Update"
            placeholder="What is happening, in plain words — this reaches every affected contact at once…"
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="hf-row" style={{ justifyContent: 'space-between', marginTop: 'var(--s-1)', gap: 'var(--s-1)', flexWrap: 'wrap' }}>
            <label className="hf-row" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
              <input type="checkbox" checked={audience === 'internal'} onChange={(e) => setAudience(e.target.checked ? 'internal' : 'affected')} />
              Keep this internal
            </label>
            <div className="hf-row" style={{ gap: 'var(--s-1)' }}>
              <Button variant="ghost" onClick={() => void send('resolved')} disabled={!body.trim() || resolve.isPending}>Resolve with this</Button>
              <Button onClick={() => void send('update')} disabled={!body.trim() || post.isPending}>Tell everyone</Button>
            </div>
          </div>
        </section>
      )}

      <section aria-label="What we have said">
        <h2 className="hf-section-title">What we have said</h2>
        {updates.length === 0 ? (
          <EmptyState title="Nothing said yet" hint="An outage nobody hears about is the one people complain about." />
        ) : (
          <ol className="hf-stack" style={{ gap: 'var(--s-2)', listStyle: 'none', padding: 0, margin: 0 }}>
            {[...updates].reverse().map((u) => (
              <li key={u.id} className="hf-card" style={{ padding: 'var(--s-2)' }}>
                <div className="hf-row hf-muted" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
                  <strong>#{u.seq}</strong>
                  <span>{when(u.published_at)}</span>
                  {u.audience === 'internal' ? <Badge tone="muted">Internal</Badge> : <Badge tone="info">{u.recipients} told</Badge>}
                  {u.is_public && <Badge tone="pos">Status page</Badge>}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{u.body}</div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {children.length > 0 && (
        <section aria-label="Same outage">
          <h2 className="hf-section-title">Same outage</h2>
          <ul className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
            {children.map((c) => (
              <li key={c.id}>
                <Link className="hf-link" to={`/tickets/${c.id}`}>{c.ref ?? c.id.slice(0, 8)} — {c.summary ?? 'Ticket'}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {signals.length > 0 && (
        <section aria-label="What the alerts said">
          <h2 className="hf-section-title">What the alerts said</h2>
          <ul className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0, fontSize: 'var(--t-12)' }}>
            {signals.map((s) => (
              <li key={s.id} className="hf-row" style={{ gap: 'var(--s-1)' }}>
                <Badge tone={s.severity === 'critical' ? 'neg' : s.severity === 'high' ? 'warn' : 'muted'}>{s.severity}</Badge>
                <span>{s.summary}</span>
                {s.asset_name && <span className="hf-muted">{s.asset_name}</span>}
                {s.occurrences > 1 && <span className="hf-muted">×{s.occurrences}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {incident.problem_ticket_id && (
        <p className="hf-muted">
          The permanent fix is tracked separately —{' '}
          <Link className="hf-link" to={`/tickets/${incident.problem_ticket_id}`}>open the problem record</Link>.
        </p>
      )}
    </div>
  );
}
